/**
 * HistorialSection — sección «Historial de cambios» de la ficha (P2RED S1-3b).
 *
 * Destino del huérfano RED-3 nº 2 (ex-`EditHistoryPanel`): historial POR
 * ELEMENTO embebido en el acordeón de la ficha, no panel flotante. Consume el
 * mismo Worker `GET /coord-edits?infraId=` (Bearer Firebase) que el legado.
 *
 * DOP-S1-3b-2=a: SOLO LECTURA en esta rebanada (fecha · antes→después ·
 * Δscore · fuente). El «deshacer puntual» decidido en M2 llega con S1-4,
 * usando el mismo canal de escritura que el conversor de coordenadas.
 * El historial de sesión de propuestas/reglas no se persiste hasta S4
 * (`OBS-P2-PROPUESTAS-PERSISTENCIA-MOTOR`) — aquí solo lo persistido.
 *
 * Skin DS v2, sin glow (regla dura). Guard de montaje (patrón SSCA-FIX-1).
 *
 * @version 1.0.0 (S1-3b)
 */

import { useCallback, useEffect, useState, type CSSProperties, type FC } from 'react';
import { colors, typography, spacing } from '@/design-system/v2';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMounted } from '@/hooks/useIsMounted';
import { WORKER_BASE_RUNTIME } from '@/lib/config/workerEndpoints';

// ============================================================================
// TYPES
// ============================================================================

/** Registro del Worker `GET /coord-edits?infraId=X` (mismo contrato GIS-D05). */
export interface CoordEditRecord {
  id: string;
  infra_id: string;
  ine5: string;
  user_email: string;
  edited_at: string; // ISO 8601
  geom_before: { x: number; y: number } | null;
  geom_after: { x: number; y: number };
  score_before: number | null;
  score_after: number | null;
  edit_source: 'MANUAL' | 'ASSISTED' | 'GEOCODER';
}

export interface HistorialSectionProps {
  /** ID de la infraestructura cuyo historial se consulta. */
  infraId: string;
}

// ============================================================================
// HELPERS
// ============================================================================

const FUENTE_LABEL: Record<CoordEditRecord['edit_source'], string> = {
  MANUAL: 'manual',
  ASSISTED: 'asistida',
  GEOCODER: 'geocodificador',
};

const fmtFecha = (iso: string): string => {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtCoord = (v: number): string => Math.round(v).toLocaleString('es-ES');

const fmtPar = (g: { x: number; y: number }): string => `${fmtCoord(g.x)}/${fmtCoord(g.y)}`;

const deltaScore = (before: number | null, after: number | null): string | null => {
  if (before == null || after == null) return null;
  const d = after - before;
  return d === 0 ? '±0' : d > 0 ? `+${d}` : `${d}`;
};

// ============================================================================
// STYLES (DS v2 · sin glow)
// ============================================================================

const listaStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacing[2],
  fontSize: typography.fontSize.xs,
};

const filaStyle: CSSProperties = {
  display: 'flex',
  gap: spacing[2],
  alignItems: 'baseline',
};

const fechaStyle: CSSProperties = {
  flex: '0 0 auto',
  fontFamily: typography.fontFamily.mono,
  color: colors.fg.muted,
};

const cuerpoFilaStyle: CSSProperties = {
  color: colors.fg.secondary,
  minWidth: 0,
  wordBreak: 'break-word',
};

const monoStyle: CSSProperties = {
  fontFamily: typography.fontFamily.mono,
};

const antesStyle: CSSProperties = {
  ...monoStyle,
  color: colors.fg.muted,
  textDecoration: 'line-through',
};

const estadoInfoStyle: CSSProperties = {
  margin: 0,
  fontSize: typography.fontSize.xs,
  color: colors.fg.muted,
};

const errorStyle: CSSProperties = {
  ...estadoInfoStyle,
  color: colors.error,
};

// ============================================================================
// COMPONENT
// ============================================================================

type Estado =
  | { fase: 'cargando' }
  | { fase: 'sin-sesion' }
  | { fase: 'error'; mensaje: string }
  | { fase: 'ok'; registros: CoordEditRecord[] };

export const HistorialSection: FC<HistorialSectionProps> = ({ infraId }) => {
  const { user, isDevBypass } = useAuth();
  const isMounted = useIsMounted();
  const [estado, setEstado] = useState<Estado>({ fase: 'cargando' });

  const cargar = useCallback(async () => {
    // HIST: bajo el bypass de desarrollo no hay sesión real de Firebase. El
    // Worker exige un token auténtico y responde 401 sin cabecera y 403 con un
    // marcador (medido el 21-ago contra geodata.pteltool.es), así que la
    // petición está condenada. Se declara el estado y no se viaja a la red.
    if (isDevBypass) {
      setEstado({ fase: 'sin-sesion' });
      return;
    }

    // Guard de bail-out: si ya estamos en 'cargando', devolver la MISMA
    // referencia corta un potencial bucle efecto→setState→render cuando la
    // identidad de `user` no es estable (el legado se salva por usar estado
    // primitivo; aquí el estado es un objeto y el guard es explícito).
    setEstado((prev) => (prev.fase === 'cargando' ? prev : { fase: 'cargando' }));
    try {
      let headers: Record<string, string> = { Accept: 'application/json' };
      if (user) {
        const token = await user.getIdToken();
        headers = { ...headers, Authorization: `Bearer ${token}` };
      }
      const url = `${WORKER_BASE_RUNTIME}/coord-edits?infraId=${encodeURIComponent(infraId)}`;
      const res = await fetch(url, { method: 'GET', headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { edits?: CoordEditRecord[] } | CoordEditRecord[];
      const registros = Array.isArray(data) ? data : (data.edits ?? []);
      if (isMounted()) setEstado({ fase: 'ok', registros });
    } catch (err) {
      if (isMounted()) {
        setEstado({
          fase: 'error',
          mensaje: err instanceof Error ? err.message : 'Error desconocido',
        });
      }
    }
  }, [infraId, user, isMounted, isDevBypass]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (estado.fase === 'cargando') {
    return (
      <p style={estadoInfoStyle} data-testid="historial-cargando">
        Cargando historial…
      </p>
    );
  }

  if (estado.fase === 'sin-sesion') {
    return (
      <p style={estadoInfoStyle} data-testid="historial-sin-sesion">
        El historial requiere una sesión iniciada. Con el modo de desarrollo sin
        autenticación activo no puede consultarse.
      </p>
    );
  }

  if (estado.fase === 'error') {
    return (
      <p style={errorStyle} data-testid="historial-error">
        No se pudo cargar el historial ({estado.mensaje}).
      </p>
    );
  }

  if (estado.registros.length === 0) {
    return (
      <p style={estadoInfoStyle} data-testid="historial-vacio">
        Este elemento no tiene ediciones de coordenadas registradas.
      </p>
    );
  }

  return (
    <div style={listaStyle} data-testid="historial-lista">
      {estado.registros.map((r) => {
        const delta = deltaScore(r.score_before, r.score_after);
        return (
          <div key={r.id} style={filaStyle} data-testid="historial-fila">
            <span style={fechaStyle}>{fmtFecha(r.edited_at)}</span>
            <span style={cuerpoFilaStyle}>
              Coordenada{' '}
              <span style={monoStyle}>
                {r.geom_before != null && (
                  <>
                    <del style={antesStyle}>{fmtPar(r.geom_before)}</del>
                    {' → '}
                  </>
                )}
                {fmtPar(r.geom_after)}
              </span>
              {' · '}
              {FUENTE_LABEL[r.edit_source] ?? r.edit_source}
              {delta != null && (
                <span style={{ color: colors.fg.muted }}> · score {delta}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default HistorialSection;
